import { useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useAuth, SignInCancelledError } from "@/lib/auth";

export function LoginScreen() {
  const auth = useAuth();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [passkeyPending, setPasskeyPending] = useState(false);

  // TNK-160 — when the LoginScreen mounts because of a 401 mid-session
  // (apiClient.notifyUnauthorized → AuthContext logout) or a deep link
  // visited without auth, the URL the operator was trying to reach is
  // still on `window.location`. Capture it once on first render so the
  // hint stays stable across re-renders during the sign-in dance, then
  // surface a short note that sign-in keeps them in context. The actual
  // restoration happens automatically — `__root.tsx` swaps LoginScreen
  // back out for the normal layout once `auth.isAuthenticated` flips,
  // and TanStack Router shows the child route matching the URL (which
  // never changed).
  const returnPathRef = useRef<string | null>(null);
  if (returnPathRef.current === null && typeof window !== "undefined") {
    const path = window.location.pathname + window.location.search;
    returnPathRef.current = path && path !== "/" ? path : "";
  }
  const returnPath = returnPathRef.current ?? "";

  async function handleEbaySignIn() {
    setError(null);
    setPending(true);
    try {
      await auth.signInWithEbay();
    } catch (e) {
      if (e instanceof SignInCancelledError) return;
      setError(e instanceof Error ? e.message : "Sign-in failed.");
    } finally {
      setPending(false);
    }
  }

  async function handlePasskeySignIn() {
    setError(null);
    setPasskeyPending(true);
    try {
      await auth.signInWithPasskey();
    } catch (e) {
      // A user cancelling the browser passkey prompt throws — don't alarm them.
      if (e instanceof DOMException && e.name === "NotAllowedError") return;
      setError(e instanceof Error ? e.message : "Passkey sign-in failed.");
    } finally {
      setPasskeyPending(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border bg-card p-8 shadow-lg">
        <header className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold">TCG Manager</h1>
          <p className="text-sm text-muted-foreground">
            Sign in with eBay to manage your inventory and listings.
          </p>
        </header>

        {returnPath && (
          <div className="flex items-start gap-2 rounded-md border border-secondary/40 bg-secondary/10 px-3 py-2 text-xs text-secondary-foreground">
            <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              We'll return you to{" "}
              <code className="rounded bg-background/60 px-1 py-0.5 font-mono text-[11px]">
                {returnPath}
              </code>{" "}
              after sign-in.
            </span>
          </div>
        )}

        <div className="space-y-3">
          <button
            type="button"
            onClick={handleEbaySignIn}
            disabled={pending}
            className="w-full rounded-md bg-primary px-4 py-2.5 font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {pending ? "Waiting for eBay…" : "Sign in with eBay"}
          </button>
          {pending && (
            <button
              type="button"
              onClick={() => auth.cancelSignIn()}
              className="w-full rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              Cancel
            </button>
          )}

          <button
            type="button"
            onClick={handlePasskeySignIn}
            disabled={passkeyPending}
            className="w-full rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            {passkeyPending ? "Waiting for passkey…" : "Sign in with a passkey"}
          </button>

          {/* Dev-only guest bypass — stripped from production builds. */}
          {import.meta.env.DEV && (
            <>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <div className="h-px flex-1 bg-border" />
                <span>dev only</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <button
                type="button"
                onClick={() => auth.loginAsGuest()}
                className="w-full rounded-md border border-dashed border-border px-4 py-2 text-sm hover:bg-muted"
              >
                Continue as guest (dev)
              </button>
            </>
          )}
        </div>

        {error && (
          <p className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
